import React, {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  badgeClasses,
  Box,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

import { getFormValidation } from "src/Shared/Components";
import { fields } from "./fields";
import { useStore } from "src/Shared/Hooks";
import { EDITOR_TYPES } from "src/Shared/Enums";
import CommentService from "../../Services/Comment.service";
import { finalize } from "rxjs";
import { IResult } from "src/Shared/Interfaces";
import { ToastService } from "src/Shared/Services";
import { AxiosError } from "axios";
import { ICommentFilter } from "../../Interfaces";
import ViewInputControl from "src/Shared/Components/ViewInputControl/ViewInputControl";
import { observer } from "mobx-react";
import { useStyles } from "./AddComment.styles";
import useDebounce from "src/Shared/Hooks/UseDebounce.hook";
import { CommentType } from "../../Enums/CommentType.enum";

type Props = {
  companyRowID: string;
  isSlaRelated: boolean;
  //unsavedComments?: any[];
  // onSlaCommentChange?: React.Dispatch<React.SetStateAction<any[]>>;
};

export type AddCommentsRef = {
  submitForm: () => void;
};

const AddComments = forwardRef<AddCommentsRef, Props>(
  ({ companyRowID, isSlaRelated }, ref) => {
    const {
      rootStore: {
        addCommentStore: {
          commentList,
          getCommentList,
          gridLoading,
          setBtnLoading,
        },
      },
    } = useStore();

    const [search, setSearch] = useState("");

    const commentForm = useMemo(() => getFormValidation(fields), []);

    const getField = (key: string) => commentForm.$(key);

    const classes = useStyles();

    const [updateCommentList, setUpdateCommentList] = useState<any[]>([]);

    useEffect(() => {
      setUpdateCommentList(commentList);
    }, [commentList]);

    useEffect(() => {
      getCommentList(companyRowID, search, isSlaRelated);
    }, [companyRowID]);

    useDebounce(
      () => {
        getCommentList(companyRowID, search, isSlaRelated);
      },
      500,
      [search],
    );

    const onSubmitForm = () => {
      if (!commentForm.isValid) {
        commentForm.showErrors();
        return;
      }

      const request: ICommentFilter = {
        ...commentForm.values(),
        companyRowID,
        commentTypeID: isSlaRelated ? CommentType.SLA : CommentType.Normal,
      };

      setBtnLoading(true);

      CommentService.addComment(request)
        .pipe(
          finalize(() => {
            setBtnLoading(false);
          }),
        )
        .subscribe({
          next: (data: IResult<string>) => {
            commentForm.clear();

            getCommentList(companyRowID, search, isSlaRelated);

            ToastService.success(data.message);
          },
          error: (error: AxiosError) => ToastService.axiosError(error),
        });
    };

    // ⭐ Expose function to modal
    useImperativeHandle(ref, () => ({
      submitForm: onSubmitForm,
    }));

    const toggleReadMore = (value: number) => {
      const updated = updateCommentList.map((x) => {
        if (x.companyCommentID === value) {
          return {
            ...x,
            isExpanded: !x.isExpanded,
          };
        }
        return x;
      });

      setUpdateCommentList(updated);
    };

    return (
      <Box className={classes.main}>
        <TextField
          sx={{ mb: 2 }}
          fullWidth
          value={search}
          label="Search Comments..."
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            endAdornment: gridLoading ? <CircularProgress size={20} /> : null,
          }}
        />

        <Box className={classes.root}>
          {updateCommentList?.map((value) => (
            <Container
              className={classes.commentsGrid}
              fixed
              key={value.companyCommentID}
            >
              <Grid container xs={12} className={classes.comments}>
                <Grid item xs={8}>
                  <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">
                     <span style={{ fontWeight: 600 }}>
                        {value.createdBy}
                      </span>{" "}
                      ({value.createdOn}):
                  </Typography>

                  {value.commentTypeID == CommentType.SLA && <Box
                    className={`${classes.slaComment} ${badgeClasses.colorSuccess}`}
                  >
                    SLA
                  </Box>}
                  {value.commentTypeID == CommentType.PriceReview && (
                    <Box className={`${classes.slaPriceReview} ${badgeClasses.colorSuccess}`}>
                      Price Review
                    </Box>
                  )}

                </Box>
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle1">
                    {value.isExpanded
                      ? value.comment.slice(0, 230)
                      : value.comment}

                    {value.comment.length >= 230 && (
                      <span
                        className={classes.readMore}
                        onClick={() => toggleReadMore(value.companyCommentID)}
                      >
                        {value.isExpanded ? "...Read more" : "...Show less"}
                      </span>
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Container>
          ))}
        </Box>

        <Box sx={{ mt: 2 }}>
          <ViewInputControl
            type={EDITOR_TYPES.TEXT_FIELD}
            rows={3}
            form={commentForm}
            field={getField("comment")}
            multiline
          />
        </Box>
      </Box>
    );
  },
);

export default observer(AddComments);
